import React, { type ReactNode } from "react";
import { captureException } from "@freightsimple/observability/errorReporting";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | undefined;
  componentStack: string | undefined;
}

function formatStack(stack: string): string {
  if (!stack) return "";
  return stack
    .split("\n")
    .filter((line) => !line.includes("/node_modules/"))
    .join("\n");
}

function ErrorDisplay_({
  error,
  componentStack,
  onReset,
}: {
  error: Error | undefined;
  componentStack: string | undefined;
  onReset: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        margin: 32,
        padding: 40,
        borderRadius: 12,
        border: "1px solid #e53e3e",
        backgroundColor: "white",
        boxShadow: "0 2px 4px rgba(229, 62, 62, 0.1)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <h2
        style={{
          margin: 0,
          marginBottom: 4,
          fontSize: 28,
          fontWeight: 600,
          color: "#e53e3e",
        }}
      >
        Something went wrong
      </h2>
      <p style={{ marginBottom: 32, fontSize: 14, color: "#666" }}>
        Please report this issue with the details below.
      </p>

      <h3
        style={{ marginTop: 24, marginBottom: 8, fontSize: 14, color: "#333" }}
      >
        Error Message:
      </h3>
      <pre
        style={{
          margin: "8px 0 24px",
          padding: 16,
          borderRadius: 8,
          backgroundColor: "#fff5f5",
          fontFamily: "Consolas, Monaco, monospace",
          fontSize: 13,
          lineHeight: 1.6,
          color: "#e53e3e",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {error?.message ?? "An unknown error occurred"}
      </pre>

      {error?.stack && (
        <>
          <h3
            style={{
              marginTop: 24,
              marginBottom: 8,
              fontSize: 14,
              color: "#333",
            }}
          >
            Stack Trace:
          </h3>
          <pre
            style={{
              margin: "8px 0 24px",
              padding: 16,
              borderRadius: 8,
              backgroundColor: "#fff5f5",
              fontFamily: "Consolas, Monaco, monospace",
              fontSize: 13,
              lineHeight: 1.6,
              color: "#e53e3e",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {formatStack(error.stack)}
          </pre>
        </>
      )}

      {componentStack && (
        <>
          <h3
            style={{
              marginTop: 24,
              marginBottom: 8,
              fontSize: 14,
              color: "#333",
            }}
          >
            Component Stack:
          </h3>
          <pre
            style={{
              margin: "8px 0 24px",
              padding: 16,
              borderRadius: 8,
              backgroundColor: "#fff5f5",
              fontFamily: "Consolas, Monaco, monospace",
              fontSize: 13,
              lineHeight: 1.6,
              color: "#e53e3e",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {formatStack(componentStack)}
          </pre>
        </>
      )}

      <button
        type="button"
        onClick={onReset}
        style={{
          marginTop: 32,
          padding: "12px 24px",
          borderRadius: 8,
          border: "none",
          backgroundColor: "#e53e3e",
          color: "white",
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Try Again
      </button>
    </div>
  );
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      componentStack: undefined,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ componentStack: errorInfo.componentStack ?? undefined });
    console.error("ErrorBoundary caught:", error, errorInfo);
    captureException(error, {
      "error.boundary": "ErrorBoundary",
      "component.stack": errorInfo.componentStack ?? "",
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: undefined,
      componentStack: undefined,
    });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorDisplay_
          error={this.state.error}
          componentStack={this.state.componentStack}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
